/**
 * The frame every page of the console is drawn inside: a sidebar down the full
 * height, a topbar across the rest, and one scrolling main region.
 *
 * ## The document does not scroll; `<main>` does
 *
 * The shell is exactly the height of the viewport and `main` is the only
 * scroller in it. This is the decision the rest of the console leans on hardest.
 * A sticky table header needs a bounded scroll container to stick inside;
 * without one, `position: sticky` measures against the document and the header
 * scrolls away with everything else on the exact tables that most need it, the
 * four hundred row ones. It also means the topbar and the sidebar stay put
 * without being `position: fixed`, so nothing has to be given a z-index to sit
 * above content it never overlaps.
 *
 * ## Below 900px the sidebar becomes a drawer
 *
 * Field staff open this on a tablet. The mode is decided in JavaScript and
 * published as `data-drawer`, rather than in a CSS media query, because the
 * shell has to know which mode it is in and not merely look like it: whether to
 * render the menu button, whether the navigation is inside a focus trap,
 * whether Escape means anything. `preferences.ts` sets out why the number
 * living in two places is the failure being avoided.
 *
 * The drawer is the same `Drawer` overlay the rest of the console uses, which
 * is what stops the navigation on a tablet from being the one surface with a
 * hand-rolled focus trap that nobody remembered to test.
 */

import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Drawer } from '@/components/overlays/Drawer/Drawer'
import {
  DRAWER_BREAKPOINT_PX,
  useConsoleChrome,
  useDensity,
  useMediaQuery,
  useScheme,
  useSidebarCollapsed,
} from '../preferences'
import { ShellContext } from '../shell'
import type { ShellState } from '../shell'
import styles from './AppShell.module.css'

export interface AppShellProps {
  /** A `<Sidebar>`. Rendered as a column, or as the drawer's content. */
  sidebar: ReactNode
  /** A `<Topbar>`. */
  topbar: ReactNode
  children: ReactNode
  className?: string
}

export function AppShell({ sidebar, topbar, children, className }: AppShellProps) {
  const [collapsed, setCollapsed] = useSidebarCollapsed()
  const [drawerRequested, setDrawerOpen] = useState(false)
  const [scheme, setScheme] = useScheme()
  const [density, setDensity] = useDensity()
  const drawer = useMediaQuery(`(max-width: ${DRAWER_BREAKPOINT_PX}px)`)

  /*
   * The shell renders the controls for the scheme and the density, so it also
   * applies them. `App` applies them too, for the public screens that have no
   * shell; `preferences.ts` sets out why calling a projection of one store
   * twice cannot conflict.
   */
  useConsoleChrome()

  /*
   * Derived rather than synchronised. Widening the window while the drawer is
   * open would otherwise leave the navigation both in the column and in an
   * overlay on top of it, with focus trapped in the copy that is no longer the
   * one being looked at.
   *
   * An effect closing it on the mode change was the first attempt, and it is
   * the wrong shape: it lets the two disagree for one render, which is one
   * render during which the trap is live over a sidebar that is also a column.
   * Asking whether the drawer is open and whether there is a drawer at all in
   * one expression means they cannot disagree.
   */
  const drawerOpen = drawer && drawerRequested

  /*
   * Collapsing is meaningless in a drawer, which is already as narrow as it is
   * going to get. Resolved once, here, and both the context and the attribute
   * below read the resolved value: putting the raw stored flag on the element
   * and the resolved one in the context leaves the collapsed rule and the
   * drawer rule both matching, and which of them wins is then a question about
   * the order two blocks happen to sit in in a stylesheet.
   */
  const collapsedNow = drawer ? false : collapsed

  const state = useMemo<ShellState>(
    () => ({
      collapsed: collapsedNow,
      setCollapsed,
      drawer,
      drawerOpen,
      setDrawerOpen,
      scheme,
      setScheme,
      density,
      setDensity,
    }),
    [collapsedNow, setCollapsed, drawer, drawerOpen, scheme, setScheme, density, setDensity],
  )

  const shellClassName = [styles.shell, className].filter(Boolean).join(' ')

  return (
    <ShellContext.Provider value={state}>
      {/*
        The class is global, from base.css. First tab stop on the page, and the
        only way past a fourteen-item sidebar for somebody who is on the
        keyboard because they have to be rather than because they prefer it.
      */}
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <div className={shellClassName} data-collapsed={collapsedNow} data-drawer={drawer}>
        {drawer ? (
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            label="Navigation"
            side="left"
            size="sm"
          >
            {sidebar}
          </Drawer>
        ) : (
          <div className={styles.sidebar}>{sidebar}</div>
        )}

        <header className={styles.topbar}>{topbar}</header>

        {/*
          `tabIndex={-1}` so the skip link can actually land here. A link to an
          element that cannot hold focus moves the scroll position and leaves
          the caret where it was, so the next Tab goes back to the second nav
          item and the skip link has achieved nothing.
        */}
        <main id="main" className={styles.main} tabIndex={-1}>
          {children}
        </main>
      </div>
    </ShellContext.Provider>
  )
}
